import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarHeart } from 'lucide-react';
import {
  FormDateField,
  FormDialog,
  FormSection,
  FormStatusField,
  FormTextareaField,
  FormTextField,
} from '@/components/common/FormDialog';
import { formatDate } from '@/utils/date';
import {
  HOLIDAY_DESCRIPTION_MAX_LENGTH,
  HOLIDAY_NAME_MAX_LENGTH,
} from '../constants/holiday.constants';
import { useSaveHoliday } from '../hooks/useHolidayMutations';
import { holidayFormSchema, type Holiday, type HolidayFormValues } from '../types/holiday.types';

function toFormValues(holiday: Holiday | null, startDate: string): HolidayFormValues {
  if (holiday === null) {
    return { dateFrom: startDate, dateTo: startDate, name: '', description: '', isActive: true };
  }
  return {
    dateFrom: holiday.dateFrom,
    dateTo: holiday.dateTo,
    name: holiday.name,
    description: holiday.description ?? '',
    isActive: holiday.isActive,
  };
}

interface HolidayFormDialogProps {
  holiday: Holiday | null;
  startDate: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HolidayFormDialog({
  holiday,
  startDate,
  open,
  onOpenChange,
}: HolidayFormDialogProps) {
  const form = useForm<HolidayFormValues>({
    resolver: zodResolver(holidayFormSchema),
    defaultValues: toFormValues(holiday, startDate ?? formatDate(new Date())),
  });

  useEffect(() => {
    if (!open) return;
    form.reset(toFormValues(holiday, startDate ?? formatDate(new Date())));
  }, [holiday, startDate, open, form]);

  const save = useSaveHoliday(() => onOpenChange(false));
  const isEditMode = holiday !== null;

  function submit(values: HolidayFormValues) {
    save.mutate({ id: holiday?.id ?? null, payload: values });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={CalendarHeart}
      title={isEditMode ? 'Edit Holiday' : 'Add Holiday'}
      description="A holiday is a day off for everyone."
      form={form}
      isPending={save.isPending}
      isEditMode={isEditMode}
      onSubmit={submit}
    >
      <FormSection>
        <FormDateField control={form.control} name="dateFrom" label="Date From" isRequired />
        <FormDateField control={form.control} name="dateTo" label="Date To" isRequired />
        <FormTextField
          control={form.control}
          name="name"
          label="Name"
          placeholder="Victory Day"
          maxLength={HOLIDAY_NAME_MAX_LENGTH}
          isRequired
          isFullRow
        />
        <FormTextareaField
          control={form.control}
          name="description"
          label="Description"
          placeholder="Optional"
          maxLength={HOLIDAY_DESCRIPTION_MAX_LENGTH}
          isFullRow
        />
      </FormSection>

      <FormStatusField
        control={form.control}
        name="isActive"
        description="Only an active holiday counts as a day off."
      />
    </FormDialog>
  );
}
